/* eslint-disable max-lines */
import api from "common/utils/api";
import { objNamesToCamelCase, objNamesToSnakeCase } from "common/utils/helpers/cases";

const MAX_MESSAGE_RETRIES = 3;
const RETRY_DELAY_MS = 1000;

function parseSseEventChunk(chunk) {
  const lines = chunk
    .split(/\r?\n/)
    .map(l => l.trimEnd())
    .filter(l => l.length > 0 && !l.startsWith(":"));

  if (lines.length === 0) return null;

  let event;
  const dataLines = [];

  for (const line of lines) {
    if (line.startsWith("event:")) {
      event = line.slice("event:".length).trim();
      // eslint-disable-next-line no-continue
      continue;
    }
    if (line.startsWith("data:")) {
      dataLines.push(line.slice("data:".length).trimStart());
    }
  }

  const dataString = dataLines.join("\n");
  if (!dataString) return null;

  try {
    return { event, data: JSON.parse(dataString) };
  } catch {
    return { event, data: dataString };
  }
}

// eslint-disable-next-line max-lines-per-function
async function attemptPinMessage(params, handlers) {
  const response = await fetch(`/api/pin_sessions/${params.sessionId}/message`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      pin_id: params.pinId,
      coordinates: params.coordinates,
      user_message: params.userMessage,
      is_first_message: params.isFirstMessage,
      prior_pins: params.priorPins.map(p => ({
        pin_id: p.pinId,
        coordinates: p.coordinates,
        state: p.state,
        instruction:
          typeof p.instruction === "string" ? p.instruction : (p.instruction?.instruction ?? "")
      }))
    })
  });

  if (!response.ok) {
    throw new Error(`Failed to submit pin message (${response.status})`);
  }

  if (!response.body) {
    throw new Error("Missing streaming response body");
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  // eslint-disable-next-line no-constant-condition
  while (true) {
    // eslint-disable-next-line no-await-in-loop
    const { value, done } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const parts = buffer.split(/\r?\n\r?\n/);
    buffer = parts.pop() ?? "";

    for (const part of parts) {
      const evt = parseSseEventChunk(part);
      // eslint-disable-next-line no-continue
      if (!evt) continue;

      if (evt.event === "acknowledgment") {
        handlers.onAcknowledgment(objNamesToCamelCase(evt.data));
      } else if (evt.event === "result") {
        handlers.onResult(objNamesToCamelCase(evt.data));
        return;
      } else if (evt.event === "error") {
        const errorPayload = objNamesToCamelCase(evt.data);
        throw new Error(errorPayload?.error || "Server error");
      }
    }
  }
}

/**
 * Submits a pin message with SSE streaming and retry logic.
 *
 * @param {object} params - { sessionId, pinId, coordinates, userMessage, isFirstMessage, priorPins }
 * @param {object} handlers - { onAcknowledgment, onResult, onError }
 * @param {object} [options] - { isPinActive?: (pinId: string) => boolean }
 */
export async function submitPinMessage(params, handlers, options = {}) {
  const { isPinActive } = options;
  let lastError = new Error("Unknown error");

  for (let attempt = 1; attempt <= MAX_MESSAGE_RETRIES; attempt += 1) {
    if (typeof isPinActive === "function" && !isPinActive(params.pinId)) return;

    try {
      // eslint-disable-next-line no-await-in-loop
      await attemptPinMessage(params, handlers);
      return;
    } catch (e) {
      lastError = e instanceof Error ? e : new Error("Unknown error");

      if (attempt < MAX_MESSAGE_RETRIES) {
        // eslint-disable-next-line no-await-in-loop
        await new Promise(resolve => {
          setTimeout(resolve, RETRY_DELAY_MS * attempt);
        });
      }
    }
  }

  handlers.onError(lastError);
}

/**
 * Creates a new pinning session.
 *
 * @param {string} artworkId
 * @param {{ previewImageName?: string }} [options]
 * @returns {Promise<{ sessionId: string }>}
 */
export async function createSession(artworkId, { previewImageName } = {}) {
  const params = { artworkId };
  if (previewImageName) {
    params.previewImageName = previewImageName;
  }
  const data = await api.designLab.pinning.createSession(objNamesToSnakeCase(params));
  return objNamesToCamelCase(data);
}

/**
 * Fetches existing pins for a session.
 *
 * @param {string} sessionId
 * @returns {Promise<Array>}
 */
export async function fetchPins(sessionId) {
  const data = await api.designLab.pinning.getPins({ id: sessionId });
  const pins = Array.isArray(data.pins) ? data.pins : [];
  const result = pins.map(p => objNamesToCamelCase(p));
  // Store updated_at if available for polling
  if (data.updatedAt !== undefined) {
    result.updatedAt = data.updatedAt;
  }
  return result;
}

/**
 * Completes a pinning session.
 *
 * @param {string} sessionId
 * @returns {Promise<{ sessionId: string, state: string, summary: string | null }>}
 */
export async function completeSession(sessionId) {
  const data = await api.designLab.pinning.completeSession(objNamesToSnakeCase({ id: sessionId }));
  return objNamesToCamelCase(data);
}

/**
 * Cancels a pinning session.
 *
 * @param {string} sessionId
 * @returns {Promise<{ sessionId: string, state: string }>}
 */
export async function cancelSession(sessionId) {
  const data = await api.designLab.pinning.cancelSession(objNamesToSnakeCase({ id: sessionId }));
  return objNamesToCamelCase(data);
}

/**
 * Removes a pin from a session.
 *
 * @param {string} sessionId
 * @param {string} pinId
 * @returns {Promise<{ pinId: string, removed: boolean }>}
 */
export async function removePin(sessionId, pinId) {
  return api.designLab.pinning.removePin({ id: sessionId, pinId });
}

/**
 * Updates the instruction text of a pin.
 *
 * @param {string} sessionId
 * @param {string} pinId
 * @param {string} instruction
 * @returns {Promise<{ success: boolean }>}
 */
export async function updatePinInstruction(sessionId, pinId, instruction) {
  const data = await api.designLab.pinning.updatePinInstruction(
    { id: sessionId, pinId },
    objNamesToSnakeCase({ instruction })
  );
  return objNamesToCamelCase(data);
}

/**
 * Bulk-deletes pins from a session.
 *
 * @param {string} sessionId
 * @param {string[]} pinIds
 * @returns {Promise<void>}
 */
export async function bulkDeletePins(sessionId, pinIds) {
  await api.designLab.pinning.bulkDeletePins({ id: sessionId }, objNamesToSnakeCase({ pinIds }));
}

/**
 * Submits feedback for a pinning session.
 *
 * @param {string} sessionId
 * @param {object} feedback - { positive, message, currentPin, currentPinState }
 */
export async function submitFeedback(sessionId, feedback) {
  await api.designLab.pinning.createFeedback({ id: sessionId }, objNamesToSnakeCase({ feedback }));
}

/**
 * Fetches the updated_at timestamp for a pinning session.
 *
 * @param {string} sessionId
 * @returns {Promise<{ sessionId: string, updatedAt: number }>}
 */
export async function fetchSessionUpdated(sessionId) {
  const data = await api.designLab.pinning.getUpdated({ id: sessionId });
  return objNamesToCamelCase(data);
}
